const state = {
    id: null,
    items: {},
    couponCode: '',
    currency: null,
    locale: null,
    initiated: false,
    queue: [],
    hooks: {
        addedToCart: [],
    },
    hooksData: {
        addedToCart: {
            product: null,
            productVariant: null,
            productVariants: [],
        },
    },
};

const getters = {
    getHookData: state => hook => {
        return state.hooksData[hook];
    },

    hasBeenInitiated: state => {
        return state.initiated;
    },

    items: state => {
        return Object.values(state.items).map(item => {
            return item;
        });
    },

    queue: state => {
        return state.queue;
    },

    getPresentmentPrice: state => variant => {
        return variant.presentment_prices.find(price => {
            return state.currency === price.currency;
        });
    },

    count: (state, getters) => {
        return getters.items.reduce((quantity, item) => {
            return quantity + item.quantity;
        }, 0);
    },

    totalPrice: (state, getters) => {
        return getters.items.reduce((total, item) => {
            let price = getters.getPresentmentPrice(item.productVariant);

            return total + price.amountInCents * item.quantity;
        }, 0);
    },

    hasVariant: (state, getters) => variant => {
        return !!getters.items.find(item => {
            return item.productVariant.id == variant.id;
        });
    },

    hasProductInQueue: state => product => {
        return !!state.queue.find(
            queueProductObject => queueProductObject.product.id === product.id
        );
    },
};

const actions = {
    addHook({ state }, { hook, name, callback }) {
        if (typeof state.hooks[hook] == 'undefined') {
            return console.error(
                `Cannot add hook. "${hook}" hook is not valid hook in the "Cart" store. Please see documentation for all available hooks.`,
                'https://docs.hlx.dev/lego/hooks'
            );
        }

        state.hooks[hook].push({ name: name, callback: callback });
    },

    removeHook({ state }, { hook, name }) {
        if (typeof state.hooks[hook] == 'undefined') {
            return console.error(
                `Cannot remove hook. "${hook}" hook is not valid hook in the "Cart" store. Please see documentation for all available hooks.`,
                'https://docs.hlx.dev/lego/hooks'
            );
        }

        let hookIndex;
        let hookExist = state.hooks[hook].some((hookee, index) => {
            hookIndex = index;
            return hookee.name == name;
        });

        if (hookExist) {
            state.hooks[hook].splice(hookIndex, 1);
        }
    },

    clearQueue({ commit }) {
        commit('clearQueue');
    },

    async addVariantToCart(
        { state, commit },
        { productVariant, product, customProperties, quantity }
    ) {
        let productVariants = [{ id: productVariant.id, quantity: quantity || 1 }];

        state.queue.forEach(queuedProductObject => {
            let variantSlug =
                typeof queuedProductObject.variantSlug !== 'undefined'
                    ? queuedProductObject.variantSlug
                    : productVariant.slug;

            let variantToAddToCart = queuedProductObject.product.variants.find(
                variant => variant.slug == variantSlug
            );

            variantToAddToCart = variantToAddToCart
                ? variantToAddToCart
                : queuedProductObject.product.variants[0];

            productVariants.push({
                id: variantToAddToCart.id,
                quantity: queuedProductObject.quantity,
            });
        });

        state.hooksData.addedToCart.product = product;
        state.hooksData.addedToCart.productVariant = productVariant;
        state.hooksData.addedToCart.addOnProductVariants = state.queue;
        (state.hooksData.addedToCart.currency = state.currency),
            await new Promise((resolve, reject) => {
                axios
                    .post('/api/cart', {
                        product_variants: productVariants,
                        cart_id: state.id,
                        coupon_code: state.couponCode,
                        custom_properties: customProperties,
                    })
                    .then(async response => {
                        commit('setCartItems', response.data.items);

                        for (let index = 0; index < state.hooks.addedToCart.length; index++) {
                            const hook = state.hooks.addedToCart[index];
                            await hook.callback();
                        }

                        resolve();
                    })
                    .catch(error => {
                        reject(error);
                    });
            });

        commit('clearQueue');
    },

    toggleProductInQueue({ state, commit, getters }, { product, quantity, variantSlug, variants }) {
        if (getters.hasProductInQueue(product)) {
            commit('removeFromQueue', product);
        } else {
            commit('addToQueue', {
                product: product,
                quantity: quantity,
                variantSlug: variantSlug,
                variants: variants,
            });
        }
    },

    addProductToQueue({ state, commit }, { product, quantity }) {
        commit('addToQueue', { product: product, quantity: quantity });
    },

    addOrUpdateProductInQueue({ commit, getters }, { product, quantity, variantSlug, variants }) {
        if (!getters.hasProductInQueue(product)) {
            return commit('addToQueue', {
                product: product,
                quantity: quantity,
                variantSlug: variantSlug,
                variants: variants,
            });
        }

        commit('removeFromQueue', product);

        return commit('addToQueue', {
            product: product,
            quantity: quantity,
            variantSlug: variantSlug,
            variants: variants,
        });
    },

    removeProductFromQueue({ state, commit }, product) {
        commit('removeFromQueue', product);
    },

    updateQueuedVariantsSlug({ state, commit }, slug) {
        commit('updateQueuedVariantsSlug', slug);
    },

    async applyCouponCode({ state, commit }, code) {
        await new Promise((resolve, reject) => {
            axios
                .post('/api/cart', {
                    cart_id: state.id,
                    coupon_code: code,
                })
                .then(response => {
                    resolve(commit('APPLY_COUPON_CODE', code));
                })
                .catch(error => {
                    reject(error);
                });
        });
    },

    async initateCart({ commit }, { id, items, currency, locale, couponCode }) {
        await commit('setCartId', id);
        await commit('setCartCurrency', currency);
        await commit('setCartLocale', locale);
        await commit('setCartItems', items);
        await commit('APPLY_COUPON_CODE', couponCode);
        await commit('setCartInitiationStatus', true);
    },
};

const mutations = {
    setCartId(state, id) {
        state.id = id;
    },

    setCartItems(state, items) {
        state.items = items;
    },

    setCartCurrency(state, currency) {
        state.currency = currency;
    },

    setCartLocale(state, locale) {
        state.locale = locale;
    },

    setCartInitiationStatus(state, status) {
        state.initiated = status;
    },

    addToQueue(state, { product, quantity, variantSlug, variants }) {
        const variant = variants.filter(variant => variant.slug == variantSlug)[0];

        if (!variant.inventory.in_stock) {
            return 'Not allowed - product is not in stock.';
        }

        state.queue.push({
            product: product,
            quantity: quantity,
            variantSlug: variantSlug,
            variants: variants,
        });
    },

    removeFromQueue(state, product) {
        state.queue = state.queue.filter(
            queuedProductObject => queuedProductObject.product.id != product.id
        );
    },

    /**
     * Find addon variant that matches or patially maches the given slug.
     *
     * @param state
     * @param slug
     */
    updateQueuedVariantsSlug(state, slug) {
        const queue = state.queue.map(addon => {
            // First look for a full match.
            const addonVariantsWithFulllMatchingSlug = addon.variants.filter(
                variant => slug === variant.slug
            );

            // Use full match if found, otherwise look for a partial match.
            const matchingVariants =
                addonVariantsWithFulllMatchingSlug.length > 0
                    ? addonVariantsWithFulllMatchingSlug
                    : addon.variants.filter(variant => slug.includes(variant.slug));

            // Select the first (and hopefully only one) or if no matches found, select the first of the variants.
            const match = matchingVariants.length > 0 ? matchingVariants[0] : addon.variants[0];

            // Remove the queued variant if it is not in stock.
            if (!match.inventory.in_stock) {
                return null;
            }

            // Update the variantSlug to that of the matched variant.
            addon.variantSlug = match.slug;

            return addon;
        });

        // Remove any empty variants from the queue.
        state.queue = queue.filter(variant => !!variant);
    },

    clearQueue(state) {
        state.queue = [];
    },

    APPLY_COUPON_CODE(state, code) {
        state.couponCode = code;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
